import React, { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Avatar,
  Text,
  Heading,
  Button,
  IconButton,
  HStack,
  VStack,
  Icon,
  Tooltip,
  Alert,
  AlertIcon,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Divider,
  Tag,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
} from "@chakra-ui/react";
import { ArrowUpIcon } from "@chakra-ui/icons";
import { formatDistanceToNow, parseISO } from "date-fns";

import {
  MdVerified as VerifiedIcon,
  SentimentSatisfiedIcon,
  VisibilityIcon,
  FavoriteBorderIconReact,
  FavoriteIconReact,
  FaChevronLeft as ChevronLeftIconReact,
  FaChevronRight as ChevronRightIconReact,
} from "../components/icons";

import {
  useGetForumPostById,
  useGetCommentsForPost,
} from "../apis/experiences";
import {
  CommentWithFullAuthor,
  Author as PostAuthorType,
  TMediaItem,
} from "../apis/types";
import { Property } from "csstype";
import CommentsSection from "../components/meet/CommentsSection";
import { useAppSelector } from "../hooks/useAppSelector";

const HeartIconSolid: React.FC<{ boxSize?: string | number }> = ({
  boxSize = "16px",
}) => <FavoriteIconReact size={boxSize} />;
const HeartIconOutline: React.FC<{ boxSize?: string | number }> = ({
  boxSize = "16px",
}) => <FavoriteBorderIconReact size={boxSize} />;

const commonEmojis = [
  "😀",
  "😂",
  "👍",
  "❤️",
  "🙌",
  "🔥",
  "🤔",
  "😊",
  "🥳",
  "🤯",
  "😭",
  "😡",
  "🙏",
  "💯",
  "✅",
  "💡",
  "🚀",
  "🌟",
  "💬",
  "👀",
];

const getAuthorLocationString = (author?: PostAuthorType): string => {
  if (!author) return "";
  const specialty = author.specialty?.[0];
  const c = author.clinics?.[0];
  const loc =
    c?.city && c?.state ? `${c.city}, ${c.state}` : c?.city || c?.state || "";
  if (specialty && loc) return `${specialty} in ${loc}`;
  return specialty || loc || "";
};

const formatCount = (n?: number) => {
  if (!n || n < 0) return "0";
  if (n < 1_000) return `${n}`;
  if (n < 1_000_000) return `${Math.floor(n / 100) / 10}K`;
  if (n < 1_000_000_000) return `${Math.floor(n / 100_000) / 10}M`;
  return `${Math.floor(n / 100_000_000) / 10}B`;
};

interface SingleMediaDisplayProps {
  mediaItem: TMediaItem;
  onClick?: () => void;
  h?: string | object;
}
const SingleMediaDisplay: React.FC<SingleMediaDisplayProps> = React.memo(
  ({ mediaItem, onClick, h = "100%" }) => {
    const objectFitValue: Property.ObjectFit = "cover";

    const commonProps = {
      w: "100%",
      h: h,
      objectFit: objectFitValue,
      cursor: onClick ? "pointer" : "default",
      onClick: onClick,
      borderRadius: "md",
      transition: "transform 0.2s ease-in-out",
      _hover: onClick
        ? {
            transform: "scale(1.03)",
          }
        : {},
    };

    if (mediaItem.type === "image") {
      return <Image src={mediaItem.url} alt="Post media" {...commonProps} />;
    } else if (mediaItem.type === "video") {
      return <Box as="video" src={mediaItem.url} controls {...commonProps} />;
    }
    return <Text>Unsupported media type</Text>;
  }
);

const PostDetailPage: React.FC = () => {
  const { postId } = useParams<{ authorId?: string; postId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const commentsSectionRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);

  const { doctorInfo } = useAppSelector((s) => s.auth);
  const currentDoctorId = doctorInfo?._id;

  const {
    data: postData,
    isLoading: isLoadingPost,
    isError: isErrorPost,
  } = useGetForumPostById(postId);
  const { data: commentsData } = useGetCommentsForPost(postId);

  const {
    isOpen: isMediaModalOpen,
    onOpen: onMediaModalOpen,
    onClose: onMediaModalClose,
  } = useDisclosure();
  const [modalMediaItems, setModalMediaItems] = useState<TMediaItem[]>([]);
  const [currentModalImageIndex, setCurrentModalImageIndex] = useState(0);

  const [currentPostMediaIndex, setCurrentPostMediaIndex] = useState(0);
  const postMediaTouchStartX = useRef(0);
  const postMediaTouchEndX = useRef(0);
  const minSwipeDistance = 50;

  const [commentText, setCommentText] = useState("");
  const [optimisticIsLiked, setOptimisticIsLiked] = useState(false);
  const [optimisticLikesCount, setOptimisticLikesCount] = useState(0);

  useEffect(() => {
    if (postData) {
      const liked =
        currentDoctorId && postData.upvotedBy?.includes(currentDoctorId);
      setOptimisticIsLiked(!!liked);
      setOptimisticLikesCount(postData.upvotedBy?.length || 0);
      setCurrentPostMediaIndex(0);
    }
  }, [postData, currentDoctorId]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (location.hash === "#comments-section" && commentsSectionRef.current) {
      setTimeout(() => {
        commentsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        commentInputRef.current?.focus();
      }, 100);
    }
  }, [location.hash]);

  const handleMainPostLikeClick = () => {
    const newLikedState = !optimisticIsLiked;
    setOptimisticIsLiked(newLikedState);
    setOptimisticLikesCount((prev) => (newLikedState ? prev + 1 : prev - 1));
  };
  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCommentText(e.target.value),
    []
  );
  const handleEmojiSelect = useCallback((emoji: string) => {
    setCommentText((prev) => prev + emoji);
    commentInputRef.current?.focus();
  }, []);
  const handleSendComment = useCallback(() => {
    if (commentText.trim() && postData?._id) {
      setCommentText("");
    }
  }, [commentText, postData?._id, currentDoctorId]);

  const handleMediaClickForModal = useCallback(
    (clickedIndexInAllMedia: number) => {
      if (postData?.media) {
        setModalMediaItems(postData.media);
        setCurrentModalImageIndex(clickedIndexInAllMedia);
        onMediaModalOpen();
      }
    },
    [postData?.media, onMediaModalOpen]
  );

  const handleModalNext = useCallback(
    () =>
      setCurrentModalImageIndex((prev) => (prev + 1) % modalMediaItems.length),
    [modalMediaItems]
  );
  const handleModalPrev = useCallback(
    () =>
      setCurrentModalImageIndex(
        (prev) => (prev - 1 + modalMediaItems.length) % modalMediaItems.length
      ),
    [modalMediaItems]
  );

  const handleScrollToCommentsAndFocusInput = useCallback(() => {
    commentsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    commentInputRef.current?.focus();
  }, []);

  const handlePostMediaNext = useCallback(() => {
    if (postData?.media) {
      setCurrentPostMediaIndex((prev) => (prev + 1) % postData.media!.length);
    }
  }, [postData?.media]);

  const handlePostMediaPrev = useCallback(() => {
    if (postData?.media) {
      setCurrentPostMediaIndex(
        (prev) => (prev - 1 + postData.media!.length) % postData.media!.length
      );
    }
  }, [postData?.media]);

  const handlePostMediaTouchStart = (e: React.TouchEvent) => {
    if (!postData?.media || postData.media.length <= 1) return;
    postMediaTouchEndX.current = 0;
    postMediaTouchStartX.current = e.touches[0].clientX;
  };

  const handlePostMediaTouchMove = (e: React.TouchEvent) => {
    if (!postData?.media || postData.media.length <= 1) return;
    postMediaTouchEndX.current = e.touches[0].clientX;
  };

  const handlePostMediaTouchEnd = () => {
    if (
      !postData?.media ||
      postData.media.length <= 1 ||
      !postMediaTouchStartX.current ||
      !postMediaTouchEndX.current
    )
      return;
    const distance = postMediaTouchStartX.current - postMediaTouchEndX.current;

    if (distance > minSwipeDistance) handlePostMediaNext();
    else if (distance < -minSwipeDistance) handlePostMediaPrev();

    postMediaTouchStartX.current = 0;
    postMediaTouchEndX.current = 0;
  };

  const isLoading = isLoadingPost;
  const isError = isErrorPost;

  if (isLoading) {
    return (
      <>
        <Box py={0} px={0} bg="gray.100" minH="100vh">
          <Box bg="white" w="100%">
            <Flex
              align="center"
              p={4}
              bg="gray.200"
              borderBottomWidth="1px"
              borderColor="gray.300"
            >
              <SkeletonCircle size="24px" mr={2} />
              <SkeletonCircle size="40px" mr={3} />
              <VStack align="flex-start" spacing={1} flex={1}>
                <SkeletonText
                  noOfLines={1}
                  skeletonHeight="16px"
                  width="150px"
                />
                <SkeletonText
                  noOfLines={1}
                  skeletonHeight="12px"
                  width="200px"
                />
              </VStack>
              <SkeletonText noOfLines={1} skeletonHeight="12px" width="50px" />
            </Flex>

            <Flex
              direction={{ base: "column", md: "row" }}
              p={{ base: 3, md: 4 }}
              gap={{ base: 4, md: 6 }}
            >
              <VStack
                flex={{ base: "1", md: 2 }}
                align="stretch"
                spacing={4}
                justifyContent="space-between"
              >
                <VStack align="stretch" spacing={4} flexGrow={1}>
                  <SkeletonText
                    noOfLines={1}
                    skeletonHeight="28px"
                    width="70%"
                  />
                  <SkeletonText
                    noOfLines={4}
                    spacing="3"
                    skeletonHeight="16px"
                  />
                  <HStack spacing={2}>
                    <Skeleton height="24px" width="80px" borderRadius="full" />
                    <Skeleton height="24px" width="100px" borderRadius="full" />
                  </HStack>
                </VStack>
                <VStack spacing={3} width="100%" pt={4}>
                  <Skeleton height="1px" width="100%" />
                  <Flex align="center" gap={3} width="100%" pt={3}>
                    <Skeleton height="40px" width="100px" borderRadius="full" />
                    <Skeleton height="40px" flex={1} borderRadius="full" />
                    <SkeletonCircle size="40px" />
                    <SkeletonText
                      noOfLines={1}
                      skeletonHeight="20px"
                      width="30px"
                    />
                  </Flex>
                </VStack>
              </VStack>
              <VStack
                flex={{ base: "none", md: 1 }}
                w={{ base: "100%", md: "auto" }}
                minW={{ base: "auto", md: "300px" }}
                maxW={{ base: "100%", md: "400px" }}
                spacing={2}
                alignSelf={{ base: "center", md: "stretch" }}
                position="sticky"
                top={{ base: "auto", md: "20px" }}
              >
                <Skeleton
                  height={{ base: "280px", md: "350px" }}
                  aspectRatio={16 / 9}
                  borderRadius="lg"
                  w="100%"
                />
                <HStack justifyContent="center" spacing={2} w="100%">
                  <SkeletonCircle size="8px" />
                  <SkeletonCircle size="8px" />
                  <SkeletonCircle size="8px" />
                </HStack>
              </VStack>
            </Flex>
          </Box>

          <Box
            w="100%"
            px={{ base: 3, md: 4 }}
            pt={6}
            pb={6}
            bg="white"
            mt={{ base: 2, md: 4 }}
          >
            <SkeletonText
              noOfLines={1}
              skeletonHeight="24px"
              width="200px"
              mb={4}
              px={{ base: 1, md: 0 }}
            />
            <VStack spacing={3} align="stretch">
              {[1, 2].map((n) => (
                <Box key={n} bg="white" p={3} borderRadius="lg" boxShadow="sm">
                  <Flex align="flex-start">
                    <SkeletonCircle size="32px" mr={3} mt={1} />
                    <VStack align="flex-start" spacing={2} flex={1}>
                      <SkeletonText
                        noOfLines={1}
                        skeletonHeight="14px"
                        width="120px"
                      />
                      <SkeletonText
                        noOfLines={1}
                        skeletonHeight="12px"
                        width="180px"
                      />
                      <SkeletonText
                        noOfLines={2}
                        spacing="2"
                        skeletonHeight="14px"
                      />
                      <HStack spacing={3} mt={2}>
                        <Skeleton height="20px" width="60px" />
                        <Skeleton height="20px" width="60px" />
                      </HStack>
                    </VStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>
      </>
    );
  }
  if (isError || !postData) {
    return (
      <>
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="calc(100vh - 120px)"
          py={10}
          px={6}
        >
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="md"
            p={6}
          >
            <AlertIcon boxSize="40px" mr={0} />
            <Heading as="h2" size="md" mt={4} mb={2}>
              Oops! Something went wrong.
            </Heading>
            <Text>Could not load the post. Please try again later.</Text>
          </Alert>
        </Flex>
      </>
    );
  }

  const commentsToDisplay: CommentWithFullAuthor[] = commentsData || [];
  const timeAgo = postData.createdAt
    ? formatDistanceToNow(parseISO(postData.createdAt), { addSuffix: true })
    : "just now";
  const authorName = postData.author?.name || "Anonymous User";
  const authorProfilePicture = postData.author?.avatarUrl || "";
  const isAuthorVerified = postData.author?.isVerified || false;
  const authorLocationDisplay = getAuthorLocationString(postData.author);
  const formattedViewsCount = formatCount(postData.viewCount);
  const repliesCount =
    postData.repliesCount !== undefined
      ? postData.repliesCount
      : commentsToDisplay.length;
  const currentGalleryMediaItem =
    postData.media && postData.media.length > 0
      ? postData.media[currentPostMediaIndex]
      : null;

  return (
    <>
      <Flex
        direction="column"
        align="center"
        py={0}
        px={0}
        bg="gray.100"
        minH="100vh"
      >
        <Box bg="white" w="100%">
          <Flex
            align="center"
            p={4}
            bg="gray.200"
            borderBottomWidth="1px"
            borderColor="gray.300"
          >
            <IconButton
              aria-label="Back to Meet"
              icon={<ChevronLeftIconReact />}
              variant="ghost"
              isRound
              onClick={() => navigate("/meet")}
              mr={2}
              transition="background-color 0.2s ease-out, color 0.2s ease-out"
            />
            <Avatar
              size="sm"
              name={authorName}
              src={authorProfilePicture}
              mr={3}
            />
            <VStack align="flex-start" spacing={0} flex={1} overflow="hidden">
              <HStack spacing={1.5} align="center">
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  color="gray.800"
                  noOfLines={1}
                  display="flex"
                  alignItems="center"
                >
                  {authorName}
                </Text>
                {isAuthorVerified && (
                  <Tooltip label="Verified" placement="top" hasArrow>
                    <Icon
                      as={VerifiedIcon}
                      color="blue.500"
                      boxSize="16px"
                      flexShrink={0}
                      ml="1px"
                    />
                  </Tooltip>
                )}
              </HStack>
              {(authorLocationDisplay || timeAgo) && (
                <HStack
                  spacing={1}
                  align="center"
                  wrap="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  fontSize="xs"
                  color="gray.600"
                  mt="1px"
                >
                  {authorLocationDisplay && (
                    <Text noOfLines={1}>{authorLocationDisplay}</Text>
                  )}
                  {authorLocationDisplay && timeAgo && (
                    <Text as="span" mx={0.5}>
                      •
                    </Text>
                  )}
                  {timeAgo && (
                    <Text noOfLines={1} flexShrink={0}>
                      {timeAgo}
                    </Text>
                  )}
                </HStack>
              )}
            </VStack>
            {/* May consider omitting view count here in the interim if not reflecting here */}
            {/* <HStack spacing={1} align="center" ml={2}>
              <Icon as={VisibilityIcon} color="gray.600" boxSize="14px" />
              <Text fontSize="xs" color="gray.600" fontWeight="medium">
                {formattedViewsCount}
              </Text>
            </HStack> */}
          </Flex>

          <Flex
            direction={{ base: "column", md: "row" }}
            p={{ base: 3, md: 4 }}
            gap={{ base: 4, md: 6 }}
            alignItems={{ base: "stretch", md: "flex-start" }}
          >
            <VStack
              flex={{ base: "1", md: 2 }}
              minW={0}
              align="stretch"
              spacing={4}
              justifyContent="space-between"
            >
              <VStack align="stretch" spacing={4} flexGrow={1}>
                {postData.title && (
                  <Heading
                    as="h1"
                    size="lg"
                    color="gray.800"
                    lineHeight="shorter"
                  >
                    {postData.title}
                  </Heading>
                )}
                <Text
                  whiteSpace="pre-wrap"
                  color="gray.700"
                  fontSize="md"
                  lineHeight="tall"
                >
                  {postData.content}
                </Text>
                {postData.tags &&
                  postData.tags.filter((tag) => tag && tag.trim() !== "")
                    .length > 0 && (
                    <Flex wrap="wrap" gap={2} mt={2}>
                      {postData.tags
                        .filter((tag) => tag && tag.trim() !== "")
                        .map((tag) => (
                          <Tag
                            size="md"
                            key={tag}
                            variant="solid"
                            backgroundColor="gray.100"
                            color="gray.600"
                            fontWeight="medium"
                            borderRadius="full"
                            px={3}
                            py={1}
                          >
                            {tag}
                          </Tag>
                        ))}
                    </Flex>
                  )}
              </VStack>

              <VStack spacing={3} width="100%" pt={4}>
                <Divider />
                <Flex align="center" gap={3} width="100%" pt={3}>
                  <Button
                    variant="solid"
                    backgroundColor="gray.800"
                    color="white"
                    fontWeight="medium"
                    size="md"
                    borderRadius="full"
                    px={4}
                    py={2}
                    _hover={{ bg: "gray.900", cursor: "pointer" }}
                    onClick={handleScrollToCommentsAndFocusInput}
                  >
                    <>
                      <Text as="span" fontWeight="bold">
                        {formatCount(repliesCount)}
                      </Text>{" "}
                      <Text as="span" ml={1}>
                        replies
                      </Text>
                    </>
                  </Button>

                  <InputGroup size="md" flex={1}>
                    <Input
                      ref={commentInputRef}
                      placeholder="What do you think?"
                      borderRadius="full"
                      bg="gray.50"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: "blue.300",
                        boxShadow: "0 0 0 1px #90CDF4",
                      }}
                      value={commentText}
                      onChange={handleCommentChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendComment();
                        }
                      }}
                      pr={
                        commentText.trim().length > 0
                          ? "calc(3rem + 28px)"
                          : "3rem"
                      }
                    />
                    <InputRightElement
                      width="auto"
                      right="4px"
                      pointerEvents="auto"
                    >
                      <HStack spacing={1} alignItems="center" height="100%">
                        <Popover placement="top-end">
                          <PopoverTrigger>
                            <IconButton
                              aria-label="Add emoji"
                              icon={
                                <Icon
                                  as={SentimentSatisfiedIcon}
                                  boxSize={5}
                                  color="gray.500"
                                />
                              }
                              size="sm"
                              variant="ghost"
                              isRound
                            />
                          </PopoverTrigger>
                          <PopoverContent
                            width="auto"
                            minW="200px"
                            _focus={{ boxShadow: "none" }}
                          >
                            <PopoverArrow />
                            <PopoverBody>
                              <SimpleGrid columns={5} spacing={1}>
                                {commonEmojis.map((emoji) => (
                                  <Button
                                    key={emoji}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEmojiSelect(emoji)}
                                    fontSize="xl"
                                    p={0}
                                    w="30px"
                                    h="30px"
                                  >
                                    {emoji}
                                  </Button>
                                ))}
                              </SimpleGrid>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                        {commentText.trim().length > 0 && (
                          <IconButton
                            aria-label="Send comment"
                            icon={<ArrowUpIcon />}
                            size="sm"
                            isRound
                            bg="gray.900"
                            color="white"
                            _hover={{ bg: "black" }}
                            onClick={handleSendComment}
                          />
                        )}
                      </HStack>
                    </InputRightElement>
                  </InputGroup>

                  <IconButton
                    aria-label="Like post"
                    icon={
                      optimisticIsLiked ? (
                        <HeartIconSolid boxSize="24px" />
                      ) : (
                        <HeartIconOutline boxSize="24px" />
                      )
                    }
                    variant="ghost"
                    size="md"
                    isRound
                    onClick={handleMainPostLikeClick}
                  />
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    minW="20px"
                    textAlign="left"
                  >
                    {formatCount(optimisticLikesCount)}
                  </Text>
                </Flex>
              </VStack>
            </VStack>

            {currentGalleryMediaItem && (
              <VStack
                flex={{ base: "none", md: 1 }}
                w={{ base: "100%", md: "auto" }}
                minW={{ base: "auto", md: "300px" }}
                maxW={{ base: "100%", md: "400px" }}
                spacing={2}
                alignSelf={{ base: "center", md: "stretch" }}
                position="sticky"
                top={{ base: "auto", md: "20px" }}
                maxH={{ base: "auto", md: "calc(100vh - 40px)" }}
              >
                <Box
                  position="relative"
                  w="100%"
                  h="auto"
                  aspectRatio={16 / 9}
                  bg="gray.100"
                  borderRadius="lg"
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() =>
                    handleMediaClickForModal(currentPostMediaIndex)
                  }
                  onTouchStart={handlePostMediaTouchStart}
                  onTouchMove={handlePostMediaTouchMove}
                  onTouchEnd={handlePostMediaTouchEnd}
                >
                  <SingleMediaDisplay
                    mediaItem={currentGalleryMediaItem}
                    h="100%"
                  />
                  {postData.media!.length > 1 && (
                    <>
                      <IconButton
                        aria-label="Previous media"
                        icon={<ChevronLeftIconReact />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePostMediaPrev();
                        }}
                        variant="ghost"
                        isRound
                        size="sm"
                        position="absolute"
                        left="10px"
                        top="50%"
                        transform="translateY(-50%)"
                        bg="blackAlpha.500"
                        color="white"
                        _hover={{ bg: "blackAlpha.700" }}
                        zIndex={1}
                      />
                      <IconButton
                        aria-label="Next media"
                        icon={<ChevronRightIconReact />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePostMediaNext();
                        }}
                        variant="ghost"
                        isRound
                        size="sm"
                        position="absolute"
                        right="10px"
                        top="50%"
                        transform="translateY(-50%)"
                        bg="blackAlpha.500"
                        color="white"
                        _hover={{ bg: "blackAlpha.700" }}
                        zIndex={1}
                      />
                    </>
                  )}
                </Box>
                {postData.media!.length > 1 && (
                  <HStack justifyContent="center" spacing={2} w="100%">
                    {postData.media!.map((_, index) => (
                      <Box
                        key={index}
                        as="button"
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={
                          index === currentPostMediaIndex
                            ? "blue.500"
                            : "gray.300"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPostMediaIndex(index);
                        }}
                        aria-label={`Go to media ${index + 1}`}
                        _focus={{ boxShadow: "outline" }}
                      />
                    ))}
                  </HStack>
                )}
              </VStack>
            )}
          </Flex>
        </Box>

        <Modal
          isOpen={isMediaModalOpen}
          onClose={onMediaModalClose}
          size="full"
          isCentered
        >
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="transparent" boxShadow="none">
            <Flex
              position="absolute"
              top={{ base: "10px", md: "20px" }}
              left={{ base: "10px", md: "20px" }}
              right={{ base: "10px", md: "20px" }}
              zIndex="overlay"
              alignItems="center"
              justifyContent="space-between"
            >
              <HStack spacing={3} alignItems="center">
                <Avatar
                  name={authorName}
                  src={authorProfilePicture}
                  size="sm"
                />
                <VStack align="flex-start" spacing={0}>
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "sm", md: "md" }}
                    color="white"
                  >
                    {modalMediaItems[currentModalImageIndex]?.type === "video"
                      ? "Video"
                      : "Image"}{" "}
                    from {authorName}
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    {timeAgo}
                  </Text>
                </VStack>
              </HStack>
              <ModalCloseButton
                color="white"
                size="lg"
                _hover={{ bg: "whiteAlpha.200" }}
                position="static"
              />
            </Flex>
            <ModalBody
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={0}
              position="relative"
              h="100vh"
              w="100vw"
            >
              {modalMediaItems.length > 0 &&
                modalMediaItems[currentModalImageIndex] &&
                (modalMediaItems[currentModalImageIndex]?.type === "image" ? (
                  <Image
                    src={modalMediaItems[currentModalImageIndex].url}
                    alt="Enlarged preview"
                    maxH="90vh"
                    maxW="90vw"
                    objectFit="contain"
                    onClick={onMediaModalClose}
                    cursor="pointer"
                  />
                ) : modalMediaItems[currentModalImageIndex]?.type ===
                  "video" ? (
                  <video
                    src={modalMediaItems[currentModalImageIndex].url}
                    controls
                    autoPlay
                    style={{
                      maxHeight: "90vh",
                      maxWidth: "90vw",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Text color="white">Unsupported media type</Text>
                ))}
              {modalMediaItems.length > 1 && (
                <>
                  <IconButton
                    aria-label="Previous image"
                    icon={<ChevronLeftIconReact size={28} />}
                    position="absolute"
                    left={{ base: "5px", md: "20px" }}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="overlay"
                    bg="whiteAlpha.300"
                    color="white"
                    borderRadius="full"
                    size={{ base: "md", md: "lg" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalPrev();
                    }}
                    _hover={{ bg: "whiteAlpha.500" }}
                  />
                  <IconButton
                    aria-label="Next image"
                    icon={<ChevronRightIconReact size={28} />}
                    position="absolute"
                    right={{ base: "5px", md: "20px" }}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="overlay"
                    bg="whiteAlpha.300"
                    color="white"
                    borderRadius="full"
                    size={{ base: "md", md: "lg" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalNext();
                    }}
                    _hover={{ bg: "whiteAlpha.500" }}
                  />
                </>
              )}
              {modalMediaItems.length > 1 && (
                <HStack
                  mt={4}
                  spacing={2}
                  position="absolute"
                  bottom={{ base: "10px", md: "20px" }}
                >
                  {modalMediaItems.map((_, index) => (
                    <Box
                      key={index}
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={
                        index === currentModalImageIndex ? "white" : "gray.500"
                      }
                      cursor="pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentModalImageIndex(index);
                      }}
                    />
                  ))}
                </HStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        <Box
          ref={commentsSectionRef}
          w="100%"
          px={{ base: 3, md: 4 }}
          pt={6}
          pb={6}
          bg="white"
          mt={{ base: 2, md: 4 }}
          id="comments-section"
        >
          <Heading
            as="h3"
            size="md"
            mb={4}
            color="gray.700"
            px={{ base: 1, md: 0 }}
          >
            Comments ({formatCount(repliesCount)})
          </Heading>

          {/* Threaded comments (fetch + tree + inline reply-to-reply UI) */}
          <CommentsSection postId={postId!} currentDoctorId={currentDoctorId} />
        </Box>
      </Flex>
    </>
  );
};

export default PostDetailPage;
